import React, { DetailedHTMLProps, InputHTMLAttributes, useRef, useState } from 'react';
import { ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import classNames from 'classnames';

import { icon_close_medium } from '@gn-audio/icons';
import { JabraIcon } from '@gn-audio/jss-components/components/JabraIcon/JabraIcon';

import { FileUploadViewModel } from 'src/types/fileUpload';

import { useFormTheme } from '../../contexts/FormTheme/FormThemeContext';

interface JabraFileUploadProps
  extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  jssMetadata: ValueFieldProps<FileUploadViewModel>;
}

const JabraFileUpload: React.FC<JabraFileUploadProps> = ({
  jssMetadata,
  onChange,
  ...htmlProps
}) => {
  const { field, errors } = jssMetadata;
  const {
    maxFileCount,
    allowedContentTypes,
    buttonLabel,
    placeholderText,
    placeholderFilesSelected,
  } = field.model;
  const hasAnyError = !!errors?.length;

  const { primaryTextColor, secondaryTextColor } = useFormTheme();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);

  const triggerChange = (files: FileList | null) => {
    if (!fileInputRef.current || !onChange) return;

    const event = new Event('change', {
      bubbles: true,
    }) as unknown as React.ChangeEvent<HTMLInputElement>;
    Object.defineProperty(event, 'target', {
      writable: false,
      value: { ...fileInputRef.current, files },
    });
    onChange(event);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles(event.target.files);
    triggerChange(event.target.files);
  };

  const clearFiles = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      setSelectedFiles(null);
      triggerChange(null);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const getDisplayText = () => {
    if (selectedFiles && selectedFiles.length > 0) {
      const fileCount = selectedFiles.length;
      if (fileCount === 1) {
        return selectedFiles[0].name;
      }
      return `${fileCount} ${placeholderFilesSelected}`;
    }
    return placeholderText;
  };

  return (
    <div className="relative w-full">
      <input
        {...htmlProps}
        ref={fileInputRef}
        className="sr-only"
        type="file"
        accept={allowedContentTypes || '.jpg,.pdf'}
        multiple={maxFileCount > 1}
        onChange={handleFileChange}
      />

      <div
        className={classNames('flex min-h-[3.25rem] w-full rounded-lg border', {
          'border-gray-100 bg-white': !hasAnyError,
          'border-error bg-error/20': hasAnyError,
        })}
      >
        <button
          type="button"
          onClick={handleButtonClick}
          className={classNames(
            `cursor-pointer border-0 bg-gray-100 px-4 py-3 font-semibold ${primaryTextColor} rounded-l-lg border-r border-gray-300 hover:bg-gray-200`
          )}
        >
          {buttonLabel}
        </button>

        <div
          className={classNames(
            `flex-1 px-4 py-3 text-lg ${primaryTextColor} cursor-pointer rounded-r-lg`,
            `${selectedFiles && selectedFiles.length > 0 ? '' : secondaryTextColor}`
          )}
          onClick={handleButtonClick}
        >
          {getDisplayText()}
        </div>

        {selectedFiles && selectedFiles.length > 0 && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2"
            onClick={clearFiles}
          >
            <JabraIcon Icon={icon_close_medium} />
          </button>
        )}
      </div>
    </div>
  );
};

export default JabraFileUpload;
